import { useEffect, useRef } from "react";
import type { Player, TimedEvent } from "@backstory/player";
import { PANEL_FACTORIES, PANEL_STYLES, type PanelActions, type PanelFetchers, type PanelId, type PanelRenderer, type RenderContext } from "@backstory/player";

let stylesInjected = false;
function ensureStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  const s = document.createElement("style");
  s.setAttribute("data-backstory-panel-styles", "");
  s.textContent = PANEL_STYLES;
  document.head.appendChild(s);
  stylesInjected = true;
}

export interface PanelHostProps {
  id: PanelId;
  player: Player | null;
  /** Playhead, relative ms (drives re-render). */
  time: number;
  /** Incremented when chunks load so panels re-derive. */
  version: number;
  actions: PanelActions;
  fetchers?: PanelFetchers;
  /** Live Assist: the panel input only covers the window since the agent attached. */
  live?: boolean;
  /**
   * Live Assist: events recorded before the agent attached, read back from the
   * session's stored chunks. The live stream starts at the moment of attach, so
   * without these the panels look empty for anything the user did while waiting.
   */
  priorEvents?: readonly TimedEvent[];
  /** Approximate place shown on the Application overview. */
  location?: string;
}

/**
 * Hosts a framework-free Player panel renderer inside React. The renderer owns
 * its DOM; we only call `update()` when the playhead or the timeline changes.
 */
export function PanelHost({ id, player, time, version, actions, fetchers, live = false, priorEvents, location }: PanelHostProps) {
  const ref = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<PanelRenderer | null>(null);
  const idRef = useRef<PanelId | null>(null);

  useEffect(() => {
    ensureStyles();
    const el = ref.current;
    if (!el) return;
    if (rendererRef.current && idRef.current !== id) {
      rendererRef.current.destroy();
      rendererRef.current = null;
    }
    if (!rendererRef.current) {
      rendererRef.current = PANEL_FACTORIES[id](el);
      idRef.current = id;
    }
    return () => {
      rendererRef.current?.destroy();
      rendererRef.current = null;
      idRef.current = null;
    };
  }, [id]);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r || !player) return;
    const tl = player.timeline;
    // Live chunks arrive from the moment of attach. Splicing the recorded
    // history in front of them is what lets a panel show the request the user
    // made before anyone was watching. Both halves are already sorted, and the
    // live feed never replays a seq the backfill covered.
    const seen = new Set(tl.events.map((e) => `${e.seq}:${e.idx}`));
    const events = priorEvents?.length
      ? [...priorEvents.filter((e) => !seen.has(`${e.seq}:${e.idx}`)), ...tl.events].sort((a, b) => a.t - b.t)
      : tl.events;
    const startTs = events.length ? Math.min(tl.startTs, events[0]!.t) : tl.startTs;
    const ctx: RenderContext & { player: Player } = {
      input: { events, startTs, now: tl.startTs + time, endTs: Number.isFinite(tl.endTs) ? tl.endTs : tl.startTs, live, loading: !live && !player.hydratedFully },
      actions,
      fetchers,
      meta: player.meta,
      location,
      mirror: player.mirror,
      release: player.meta?.release,
      player,
    };
    r.update(ctx);
  }, [player, time, version, actions, fetchers, id, live, priorEvents, location]);

  return <div ref={ref} className="panel-host" data-panel={id} />;
}
